import { Checkbox, Input } from "@/components/common";
import type { PaperConsentPayload } from "@/types/clients";

const MAX_BYTES = 10 * 1024 * 1024;

export type HealthChoice = "na" | "yes" | "no";

export interface PaperConsentForm {
  file: File | null;
  signedOn: string;
  clientBase: boolean;
  marketingPhone: boolean;
  marketingSms: boolean;
  marketingEmail: boolean;
  health: HealthChoice;
  confirmed: boolean;
}

export function today() {
  return new Date().toISOString().slice(0, 10);
}

export function emptyPaperForm(): PaperConsentForm {
  return {
    file: null,
    signedOn: today(),
    clientBase: false,
    marketingPhone: false,
    marketingSms: false,
    marketingEmail: false,
    health: "na",
    confirmed: false,
  };
}

function formatSize(bytes: number) {
  return bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function paperFileError(file: File | null): string | undefined {
  if (!file) {
    return undefined;
  }
  if (file.size > MAX_BYTES) {
    return `Plik ma ${formatSize(file.size)} — maksymalnie 10 MB. Zeskanuj ponownie w odcieniach szarości (150–200 dpi).`;
  }
  if (!file.name.toLowerCase().endsWith(".pdf")) {
    return "Wybierz plik PDF.";
  }
  return undefined;
}

export function isPaperFormValid(f: PaperConsentForm) {
  return !!f.file && !paperFileError(f.file) && !!f.signedOn && f.confirmed;
}

export function toPaperPayload(f: PaperConsentForm): PaperConsentPayload {
  if (!f.file) {
    throw new Error("Brak pliku");
  }
  return {
    file: f.file,
    signedOn: f.signedOn,
    clientBase: f.clientBase,
    marketingPhone: f.marketingPhone,
    marketingSms: f.marketingSms,
    marketingEmail: f.marketingEmail,
    health: f.health === "na" ? null : f.health === "yes",
  };
}

interface PaperConsentFieldsProps {
  value: PaperConsentForm;
  onChange: (value: PaperConsentForm) => void;
}

/** The signed form's scan plus the decisions the agent transcribes from it. */
export function PaperConsentFields({
  value,
  onChange,
}: PaperConsentFieldsProps) {
  const set = (patch: Partial<PaperConsentForm>) => {
    onChange({ ...value, ...patch });
  };
  const fileError = paperFileError(value.file);

  return (
    <div className="space-y-4 rounded-lg border border-edge-soft bg-subtle/50 p-4">
      <div>
        <label
          htmlFor="paper-consent-file"
          className="mb-1.5 block text-sm font-medium text-main"
        >
          Skan podpisanej zgody (PDF, maks. 10 MB)
        </label>
        <input
          id="paper-consent-file"
          type="file"
          accept="application/pdf,.pdf"
          onChange={(e) => {
            set({ file: e.target.files?.[0] ?? null });
          }}
          className="block w-full text-sm text-sub file:mr-3 file:rounded-lg file:border-0 file:bg-adara-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-adara-700 hover:file:bg-adara-100"
        />
        <p className="mt-1 text-xs text-faint">
          {value.file
            ? `${value.file.name} · ${formatSize(value.file.size)}`
            : "Wszystkie strony w jednym pliku, czytelny podpis. Skanuj w odcieniach szarości, 150–200 dpi."}
        </p>
        {fileError && <p className="mt-1 text-xs text-red-600">{fileError}</p>}
      </div>

      <Input
        label="Data podpisu"
        type="date"
        value={value.signedOn}
        max={today()}
        onChange={(e) => {
          set({ signedOn: e.target.value });
        }}
        required
      />

      <fieldset className="space-y-2">
        <legend className="mb-1 text-sm font-medium text-main">
          Decyzje zaznaczone przez klienta na formularzu
        </legend>
        <Checkbox
          label="Baza klientów — TAK"
          checked={value.clientBase}
          onChange={(e) => {
            set({ clientBase: e.target.checked });
          }}
        />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="text-sm text-sub">Marketing — TAK przez:</span>
          <Checkbox
            label="telefon"
            checked={value.marketingPhone}
            onChange={(e) => {
              set({ marketingPhone: e.target.checked });
            }}
          />
          <Checkbox
            label="SMS"
            checked={value.marketingSms}
            onChange={(e) => {
              set({ marketingSms: e.target.checked });
            }}
          />
          <Checkbox
            label="e-mail"
            checked={value.marketingEmail}
            onChange={(e) => {
              set({ marketingEmail: e.target.checked });
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="text-sm text-sub">Dane o zdrowiu:</span>
          {(
            [
              ["na", "nie dotyczy"],
              ["yes", "TAK"],
              ["no", "NIE"],
            ] as const
          ).map(([health, label]) => (
            <label
              key={health}
              className="flex items-center gap-1.5 text-sm text-sub"
            >
              <input
                type="radio"
                name="paper-health"
                className="accent-adara-700"
                checked={value.health === health}
                onChange={() => {
                  set({ health });
                }}
              />
              {label}
            </label>
          ))}
        </div>
        <p className="text-xs text-faint">
          Niezaznaczone pozycje zapisują się jako NIE — przepisz dokładnie to,
          co jest na formularzu.
        </p>
      </fieldset>

      <Checkbox
        label="Potwierdzam, że skan jest kompletny, czytelny i podpisany przez klienta."
        checked={value.confirmed}
        onChange={(e) => {
          set({ confirmed: e.target.checked });
        }}
      />
    </div>
  );
}
